import * as React from "react";
import { MapPin, GraduationCap, Wallet, ArrowRight, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const highlights = [{
  icon: MapPin,
  title: "South Florida Experts",
  description: "Specialized knowledge of local building codes and coastal conditions."
}, {
  icon: GraduationCap,
  title: "Certified Inspectors",
  description: "Licensed home inspectors with experience evaluating South Florida condominiums."
}, {
  icon: Wallet,
  title: "Protect Your Investment",
  description: "Document visible concerns before they become more costly to address."
}];
const features = ["Visual Inspection of Accessible Structural Components", "Detailed Digital Reports with Photos", "Fast 24-Hour Turnaround Time"];
export default function TajikistanHero() {
  return <section data-section-id="242400" id="hero" className="relative bg-background text-foreground pt-20 pb-16 md:pt-28 md:pb-24 lg:pt-32 lg:pb-32 overflow-hidden">
      {/* Decorative Background Glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary/10 blur-[100px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Content Column */}
          <div className="flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <Badge variant="secondary" className="px-3 py-1 text-sm bg-secondary/10 text-secondary border-secondary/20">
                <MapPin className="w-4 h-4 mr-1" />
                Serving South Florida
              </Badge>
              
              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1]">
                Professional <span className="text-primary">Condo Inspection</span> Services
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-[600px] leading-relaxed font-sans">
                Get a clearer understanding of your South Florida condominium through a detailed visual inspection of accessible systems and components. We document visible concerns involving accessible structural components, electrical systems, and plumbing.
              </p>
            </div>

            {/* Checklist */}
            <ul className="space-y-3">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-center text-foreground">
                  <CircleCheck className="w-5 h-5 text-primary mr-3 shrink-0" />
                  <span className="font-default font-medium">{feature}</span>
                </li>)}
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button size="lg" asChild className="w-full sm:w-auto text-base h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg transition-all">
                <Link to="https://trusted-fl.com/#schedule-your-inspection">
                  Book Your Inspection
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="w-full sm:w-auto text-base h-12 px-8 border-secondary text-secondary hover:bg-secondary/5 rounded-full">
                <Link href="https://trusted-fl.com/#our-inspection-services">
                  View All Services
                </Link>
              </Button>
            </div>
          </div>

          {/* Image & Highlights Column */}
          <div className="relative lg:ml-auto w-full max-w-[600px] mx-auto lg:max-w-none">
            {/* Main Visual */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50 bg-muted aspect-[4/3] lg:aspect-[3/4] xl:aspect-[4/5]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/south_florida_condo_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/south_florida_condo_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/south_florida_condo_inspection.png-wvc-sizes" alt="Professional inspector examining a South Florida condominium" className="object-cover w-full h-full" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-secondary/0 to-secondary/0" />
            </div>

            {/* Overlapping Highlights Grid/Stack */}
            <div className="absolute -bottom-6 -left-6 md:-left-12 right-6 md:right-auto flex flex-col gap-3 z-20">
              {highlights.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="bg-background/95 backdrop-blur-md border border-border shadow-xl rounded-xl p-4 flex items-start gap-4 max-w-[320px] transform transition-transform hover:-translate-y-1">
                    <div className="bg-primary/10 p-2.5 rounded-lg shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-default font-semibold text-foreground text-sm mb-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-snug font-sans">
                        {item.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}